import { AnyAction, applyMiddleware, combineReducers, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import { apiMiddleware } from 'redux-api-middleware';

import {
  copyClipConfigurationFromClientSaga,
  createNewClientSaga,
  editClientSaga,
  loadDownloadSummarySaga,
  loadActiveClientsSaga,
  loadArchivedClientsSaga,
  loadDefaultClipConfigurationSaga,
} from '../actions/clientActions';
import {
  assignEventSaga,
  createEventSaga,
  deleteParticipantsDataSaga,
  deleteParticipantsVgZipSaga,
  deleteParticipantsZipSaga,
  deleteParticipantsVgVideoSaga,
  editEventSaga,
  generateParticipantsVgZipSaga,
  generateParticipantsZipSaga,
  generateVgVideoCeremonySaga,
  loadEventSaga,
  loadActiveEventsSaga,
  loadArchivedEventsSaga,
  sendToNlpSaga,
} from '../actions/eventActions';
import { loadEventActionsSaga } from '../actions/eventActionsActions';
import { loadEventStatisticsSaga } from '../actions/eventStatisticsActions';
import { addEventTransitionSaga } from '../actions/eventTransitionActions';
import {
  addEventVideoSaga,
  loadEventArchivedVideosSaga,
  loadEventVideosSaga,
  loadUploadedVideosSaga,
} from '../actions/eventVideosActions';
import {
  addEventParticipantSaga,
  addUnknownParticipantSaga,
  convertToUnknownParticipantSaga,
  editEventParticipantSaga,
  loadEventParticipantsSaga,
  mergeParticipantsSaga,
  sendParticipantsToGeneratorSaga,
  sendParticipantsToLiveGeneratorSaga,
  uploadAegisubResultSaga,
  uploadEventParticipantsSaga,
  uploadEventParticipantsWithMergeSaga,
  uploadEventParticipantsForMarchingOrderSaga,
  uploadParticipantsWithEventSplitSaga,
  uploadParticipantsMarchingOrderDataSaga,
  loadEventArchivedParticipantsSaga,
  sendParticipantsToLiveRRESaga,
} from '../actions/participantActions';
import { attemptLogin } from '../actions/sessionActions';
import { createNewShopSaga, loadShopsSaga } from '../actions/shopActions';
import { changePasswordSaga, createUserSaga, editUserSaga, loadUserSaga, loadUsersSaga } from '../actions/userActions';
import * as NotificationHelper from '../helpers/notificationHelper';
import { appStatusReducer } from '../reducers/appStatusReducer';
import { customFormReducer } from '../reducers/customFormReducer';
import { entitiesReducer } from '../reducers/entitiesReducer';
import { errorsReducer } from '../reducers/errorsReducer';
import { singleEntityReducer } from '../reducers/singleEntityReducer';
import {
  authMiddleware,
  bodyMiddleware,
  endpointMiddleware,
  validationMiddleware,
  payloadUnwrapper,
  externalResourceMiddleware,
} from '../middlewares';
import { loadEventArchivedActionsSaga } from '../actions/eventArchivedActionsActions';

import { moSagas } from 'sagas/mo.saga';
import { clientSaga } from 'api/clients/clients.saga';

const sagaMiddleware = createSagaMiddleware();

const notificationMiddleware = () => (next: any) => (action: AnyAction) => {
  NotificationHelper.notifyOnAction(action);

  return next(action);
};

const middlewares = [
  sagaMiddleware,
  endpointMiddleware,
  authMiddleware,
  bodyMiddleware,
  apiMiddleware,
  validationMiddleware,
  notificationMiddleware,
  payloadUnwrapper,
  externalResourceMiddleware,
];

export const appStore = createStore(
  combineReducers({
    appStatus: appStatusReducer,
    entities: entitiesReducer,
    errors: errorsReducer,
    form: customFormReducer,
    singleEntity: singleEntityReducer,
  }),
  undefined,
  composeWithDevTools(applyMiddleware(...middlewares)),
);

export function registerSagas() {
  sagaMiddleware.run(moSagas);
  sagaMiddleware.run(loadUsersSaga);
  sagaMiddleware.run(loadUserSaga);
  sagaMiddleware.run(createUserSaga);
  sagaMiddleware.run(editUserSaga);
  sagaMiddleware.run(changePasswordSaga);
  sagaMiddleware.run(attemptLogin);
  sagaMiddleware.run(createNewClientSaga);
  sagaMiddleware.run(loadActiveClientsSaga);
  sagaMiddleware.run(loadArchivedClientsSaga);
  sagaMiddleware.run(createEventSaga);
  sagaMiddleware.run(editEventSaga);
  sagaMiddleware.run(loadActiveEventsSaga);
  sagaMiddleware.run(loadArchivedEventsSaga);
  sagaMiddleware.run(uploadEventParticipantsSaga);
  sagaMiddleware.run(uploadEventParticipantsWithMergeSaga);
  sagaMiddleware.run(uploadEventParticipantsForMarchingOrderSaga);
  sagaMiddleware.run(uploadParticipantsWithEventSplitSaga);
  sagaMiddleware.run(uploadParticipantsMarchingOrderDataSaga);
  sagaMiddleware.run(addEventParticipantSaga);
  sagaMiddleware.run(addUnknownParticipantSaga);
  sagaMiddleware.run(editEventParticipantSaga);
  sagaMiddleware.run(mergeParticipantsSaga);
  sagaMiddleware.run(convertToUnknownParticipantSaga);
  sagaMiddleware.run(loadEventParticipantsSaga);
  sagaMiddleware.run(loadEventArchivedParticipantsSaga);
  sagaMiddleware.run(loadEventSaga);
  sagaMiddleware.run(assignEventSaga);
  sagaMiddleware.run(addEventTransitionSaga);
  sagaMiddleware.run(loadEventVideosSaga);
  sagaMiddleware.run(loadEventArchivedVideosSaga);
  sagaMiddleware.run(loadUploadedVideosSaga);
  sagaMiddleware.run(addEventVideoSaga);
  sagaMiddleware.run(loadShopsSaga);
  sagaMiddleware.run(uploadAegisubResultSaga);
  sagaMiddleware.run(loadDefaultClipConfigurationSaga);
  sagaMiddleware.run(createNewShopSaga);
  sagaMiddleware.run(loadEventStatisticsSaga);
  sagaMiddleware.run(editClientSaga);
  sagaMiddleware.run(loadDownloadSummarySaga);
  sagaMiddleware.run(sendParticipantsToGeneratorSaga);
  sagaMiddleware.run(sendParticipantsToLiveGeneratorSaga);
  sagaMiddleware.run(copyClipConfigurationFromClientSaga);
  sagaMiddleware.run(loadEventActionsSaga);
  sagaMiddleware.run(loadEventArchivedActionsSaga);
  sagaMiddleware.run(generateParticipantsZipSaga);
  sagaMiddleware.run(deleteParticipantsZipSaga);
  sagaMiddleware.run(deleteParticipantsDataSaga);
  sagaMiddleware.run(generateParticipantsVgZipSaga);
  sagaMiddleware.run(deleteParticipantsVgZipSaga);
  sagaMiddleware.run(deleteParticipantsVgVideoSaga);
  sagaMiddleware.run(generateVgVideoCeremonySaga);
  sagaMiddleware.run(clientSaga);
  sagaMiddleware.run(sendToNlpSaga);
  sagaMiddleware.run(sendParticipantsToLiveRRESaga);
}
